import { useCallback, useEffect, useRef, useState } from 'react';
import { MpClient } from '../MpClient';
import { localSeatOf } from '../seat';
import type { ChatMessage, MatchInfo } from '../types';
import { FrameSync, type MissedFrame } from './frameSync';
import { PIP_EXPIRY_MS, PendingIntents } from './pendingIntents';
import { TickSubmitter } from './tickSubmitter';

export interface UseMpMatchOptions {
  matchInfo: MatchInfo;
  localPlayerId: string;
  /** Step the sim for N ticks and publish state. */
  externalStep: (n: number) => void;
  /** Apply intents from a server frame. */
  applyFrame: (frame: Record<string, readonly unknown[]>) => void;
  /** Get current determinism hash from the sim adapter. */
  getDeterminismHash: () => bigint;
  /** Replace the whole world with a saved one (the server's snapshot resync). */
  loadSnapshot?: (save: string) => void;
  onMatchEnd: (winnerId: string) => void;
}

export function useMpMatch({
  matchInfo,
  localPlayerId,
  externalStep,
  applyFrame,
  getDeterminismHash,
  loadSnapshot,
  onMatchEnd,
}: UseMpMatchOptions) {
  const clientRef = useRef<MpClient | null>(null);
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [chatHistory, setChatHistory] = useState<readonly ChatMessage[]>([]);
  const [determinismMismatch, setDeterminismMismatch] = useState(false);
  const syncRef = useRef<FrameSync | null>(null);
  const [absent, setAbsent] = useState<readonly string[]>([]);
  const [resyncNeeded, setResyncNeeded] = useState(false);
  const pendingRef = useRef(new PendingIntents());
  const submitterRef = useRef<TickSubmitter | null>(null);
  const earlyOrdersRef = useRef<unknown[]>([]);
  const [pending, setPending] = useState(0);
  const seatKey = String(localSeatOf(matchInfo));

  // The connection must outlive re-renders. These callbacks change identity on
  // render; listing them as effect deps tore the socket down and rebuilt it
  // mid-match — a second `player:join`, a fresh FrameSync that read the next
  // frame as a gap, and no tick submitter. Read the latest through a ref.
  const latest = useRef({ externalStep, applyFrame, getDeterminismHash, loadSnapshot, onMatchEnd });
  latest.current = { externalStep, applyFrame, getDeterminismHash, loadSnapshot, onMatchEnd };
  const { serverUrl } = matchInfo;

  useEffect(() => {
    const client = new MpClient();
    clientRef.current = client;
    let closing = false;
    const sync = new FrameSync({
      applyFrame: (f) => latest.current.applyFrame(f),
      step: () => latest.current.externalStep(1),
      getHash: () => latest.current.getDeterminismHash().toString(),
      onMismatch: (tick, local, server) => {
        if (import.meta.env.DEV) {
          console.warn(`[determinism] tick ${tick}: client=${local} server=${server} — MISMATCH`);
        }
        setDeterminismMismatch(true);
      },
      // Feeds the server's desync vote with the same pre-apply hash.
      onHash: (tick, hash) => client.reportHash(tick, hash),
    });
    syncRef.current = sync;

    const unsubs = [
      client.on('input:frame', (msg) => {
        const f = msg as unknown as MissedFrame;
        const result = sync.receive(f);
        if (result === 'gap') setResyncNeeded(true);
        if (result === 'applied') {
          pendingRef.current.landed(f.frame[seatKey]?.length ?? 0);
          setPending(pendingRef.current.count());
          submitterRef.current?.onFrameApplied(f.tick);
        }
      }),
      // Lockstep only moves when every seat speaks each tick: open tick 0 here,
      // and each applied frame opens the next.
      client.on('match:starting', (msg) => {
        const { tickRate } = msg as unknown as { tickRate?: number };
        const submitter = new TickSubmitter({
          send: (tick, actions) => client.sendIntents(tick, actions),
          intervalMs: 1000 / (tickRate && tickRate > 0 ? tickRate : 10),
        });
        submitterRef.current = submitter;
        for (const o of earlyOrdersRef.current) submitter.queue(o);
        earlyOrdersRef.current = [];
        submitter.start();
      }),
      client.on('match:end', (msg) => {
        const end = msg as unknown as { winnerId: string };
        latest.current.onMatchEnd(end.winnerId);
      }),
      client.on('chat', () => {
        setChatHistory([...client.getChatHistory()]);
      }),
      // Our own socket dropping is seen locally: the server cannot tell a
      // player it has lost the player's connection.
      client.on('disconnect', (msg) => {
        if ((msg as { source?: string }).source !== 'match-server') return;
        if (closing) return;
        setReconnecting(true);
        client.reconnect(sync.lastApplied).then((ok) => {
          if (!ok) setReconnecting(false);
        });
      }),
      client.on('player:disconnect', (msg) => {
        const { playerId } = msg as unknown as { playerId: string };
        if (playerId !== localPlayerId)
          setAbsent((a) => (a.includes(playerId) ? a : [...a, playerId]));
      }),
      client.on('player:reconnect', (msg) => {
        const { playerId } = msg as unknown as { playerId: string };
        setAbsent((a) => a.filter((p) => p !== playerId));
      }),
      client.on('rejoin', (msg) => {
        const rejoin = msg as unknown as {
          missedFrames: MissedFrame[];
          complete?: boolean;
          snapshot?: { tick: number; save: string };
        };
        // Too far behind to replay: the server sent a saved world instead.
        // Load it, continue from the frame after it, then replay the rest.
        if (rejoin.snapshot && latest.current.loadSnapshot) {
          latest.current.loadSnapshot(rejoin.snapshot.save);
          sync.resetTo(rejoin.snapshot.tick);
        }
        // Replay what the server held for us. If the window no longer reaches
        // our last frame (or a gap remains) we cannot catch up: say so rather
        // than step a world nobody else is in.
        const ok = sync.replay(rejoin.missedFrames) && rejoin.complete !== false;
        if (!ok) setResyncNeeded(true);
        else submitterRef.current?.resume(sync.lastApplied);
        setReconnecting(false);
      }),
      client.on('player:timeout', (msg) => {
        const to = msg as unknown as { playerId: string };
        if (to.playerId === localPlayerId) {
          // Our slot timed out — match is effectively over for us
          latest.current.onMatchEnd('');
        }
      }),
    ];

    client
      .connectMatchServer(serverUrl)
      .then(() => {
        if (closing) return;
        client.joinMatch(localPlayerId, localPlayerId, matchInfo.matchId);
        setConnected(true);
      })
      .catch((err: unknown) => {
        // A dev double-mount closes the first socket before it opens; that is
        // not a failure.
        if (!closing) console.error('[mp] match-server connection failed', err);
      });

    return () => {
      closing = true;
      submitterRef.current?.stop();
      submitterRef.current = null;
      for (const u of unsubs) {
        u();
      }
      client.disconnect();
    };
  }, [serverUrl, seatKey, localPlayerId]);

  const queueIntent = useCallback((intent: unknown) => {
    // Orders ride the next tick's submission (the server has no per-order message).
    if (submitterRef.current) submitterRef.current.queue(intent);
    else earlyOrdersRef.current.push(intent);
    pendingRef.current.sent();
    setPending(pendingRef.current.count());
    // Pips expire on their own; re-read once they have, or the last one sticks.
    setTimeout(() => setPending(pendingRef.current.count()), PIP_EXPIRY_MS + 50);
  }, []);

  const sendChat = useCallback(
    (text: string) => {
      clientRef.current?.sendChat(localPlayerId, text);
    },
    [localPlayerId],
  );

  return {
    connected,
    reconnecting,
    chatHistory,
    queueIntent,
    sendChat,
    client: clientRef.current,
    determinismMismatch,
    /** Other seats whose player is currently disconnected. */
    absent,
    /** My orders sent but not yet in an applied frame. */
    pending,
    /** Replay could not bring us level: reload/resync needed. */
    resyncNeeded,
  };
}
